import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './ContactForm.css'; // Reuse existing contact form styles
import './CourseRegistrationForm.css'; // Import success section styles
// Use Amplify outputs to discover REST API URL when available
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - JSON import
import outputs from '../amplify_outputs.json';
import type { Schema } from '../amplify/data/resource';

interface Props {
  variant: 'verwaltung' | 'produktion';
  initialRegion?: string;
}

interface KIWerkstattFormData {
  variant: 'verwaltung' | 'produktion';
  region: string;
  name: string;
  email: string;
  company: string;
  phone: string;
  participantCount: string;
  message: string;
  // Honeypot fields - hidden from users, bots will fill them
  website: string;
  confirm_email: string;
  // Security metadata
  formStartTime: number;
  // Privacy consent
  acceptPrivacy?: boolean;
}

interface FormErrors {
  [key: string]: string;
}

const KIWerkstattRegistrationForm: React.FC<Props> = ({ variant, initialRegion = '' }) => {
  const [formData, setFormData] = useState<KIWerkstattFormData>({
    variant: variant,
    region: initialRegion,
    name: '',
    email: '',
    company: '',
    phone: '',
    participantCount: '',
    message: '',
    // Honeypot fields - hidden from users, bots will fill them
    website: '',
    confirm_email: '',
    // Security metadata
    formStartTime: Date.now(),
    // Privacy consent
    acceptPrivacy: false,
  });

  // Sync external region selection (e.g. from map pin button)
  React.useEffect(() => {
    if (initialRegion) {
      setFormData(prev => ({ ...prev, region: initialRegion }));
    }
  }, [initialRegion]);

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
    const { name } = target as HTMLInputElement;
    const fieldValue = (target as HTMLInputElement).type === 'checkbox'
      ? (target as HTMLInputElement).checked
      : (target as HTMLInputElement).value;
    setFormData(prev => ({
      ...prev,
      [name]: fieldValue
    }));
    
    // Clear error when user starts typing/toggling
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): FormErrors => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name ist zu kurz';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Name ist zu lang (maximal 100 Zeichen)';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Firma ist erforderlich';
    } else if (formData.company.trim().length > 200) {
      newErrors.company = 'Firmenname ist zu lang (maximal 200 Zeichen)';
    }

    if (formData.phone && formData.phone.length > 50) {
      newErrors.phone = 'Telefonnummer ist zu lang';
    }

    // Variant is always set from prop, no validation needed

    if (!formData.region) {
      newErrors.region = 'Bitte wählen Sie eine Region';
    }

    const participantCountNum = parseInt(formData.participantCount);
    if (!formData.participantCount.trim()) {
      newErrors.participantCount = 'Anzahl Teilnehmer ist erforderlich';
    } else if (isNaN(participantCountNum) || participantCountNum < 1) {
      newErrors.participantCount = 'Bitte geben Sie eine gültige Anzahl ein (mindestens 1)';
    } else if (participantCountNum > 50) {
      newErrors.participantCount = 'Maximal 50 Teilnehmer möglich';
    }

    if (formData.message.trim().length > 1000) {
      newErrors.message = 'Nachricht ist zu lang (maximal 1000 Zeichen)';
    }

    // Privacy consent required
    if (!formData.acceptPrivacy) {
      newErrors.acceptPrivacy = 'Sie müssen der Datenschutzerklärung zustimmen';
    }

    // Honeypot validation
    if (formData.website || formData.confirm_email) {
      newErrors.honeypot = 'Spam detected';
    }

    // Time-based validation (form filled too quickly)
    const timeSpent = Date.now() - formData.formStartTime;
    if (timeSpent < 3000) { // Less than 3 seconds
      newErrors.timing = 'Form submitted too quickly';
    }

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Prepare variant name for email
      const variantName = formData.variant === 'produktion' 
        ? 'Deep Learning & Edge Computing' 
        : 'Lokale RAG Chatbots';

      // 1) Create Reservation record
      const { generateClient } = await import('aws-amplify/data');
      const client = generateClient<Schema>({ authMode: 'iam' });
      
      const reservationPayload = {
        variant: formData.variant,
        region: formData.region,
        name: formData.name.trim(),
        email: formData.email.trim(),
        company: formData.company.trim(),
        phone: formData.phone.trim(),
        participantCount: formData.participantCount,
        message: formData.message.trim(),
        reservationDate: new Date().toISOString(),
        status: 'pending',
        acceptedPrivacy: formData.acceptPrivacy || false,
        formSubmitDuration: Math.floor((Date.now() - formData.formStartTime) / 1000),
      };
      
      const created = await client.models.Reservation.create(reservationPayload as any);
      const reservationId = created?.data?.id;
      if (!reservationId) throw new Error('Reservation creation failed');
      
      console.log('✅ Reservation created:', reservationId);
      
      // 2) Send email notification via Lambda
      const functionUrl = (outputs as any)?.custom?.CONTACT_EMAIL_URL || process.env.REACT_APP_CONTACT_URL || '';
      if (functionUrl) {
        const emailPayload = {
          type: 'workshop_reservation',
          variant: formData.variant,
          variantName: variantName,
          region: formData.region,
          reservationId: reservationId,
          name: formData.name.trim(),
          email: formData.email.trim(),
          company: formData.company.trim(),
          phone: formData.phone.trim(),
          participantCount: formData.participantCount,
          message: formData.message.trim(),
          subject: 'KI-Werkstatt Reservierung',
          customSubject: variantName,
        };
        
        const res = await fetch(functionUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(emailPayload),
        });
        
        if (!res.ok) {
          console.warn('⚠️ Email notification failed, but reservation was saved:', res.status);
        } else {
          console.log('✅ Email notification sent');
        }
      } else {
        console.warn('⚠️ Contact function URL not configured - email not sent');
      }

      setSubmitStatus('success');
      setFormData({
        variant: variant,
        region: '',
        name: '',
        email: '',
        company: '',
        phone: '',
        participantCount: '',
        message: '',
        website: '',
        confirm_email: '',
        formStartTime: Date.now(),
        acceptPrivacy: false,
      });
    } catch (error) {
      console.error('Error submitting KI-Werkstatt reservation:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <div className="contact-form-container">
        <div className="registration-form-section success-section">
          <div className="success-content">
            <div className="success-icon"></div>
            <h3>Vielen Dank für Ihr Interesse!</h3>
            <p className="success-message">
              Ihre Registrierung für die KI-Werkstatt wurde erfolgreich übermittelt. 
              Wir werden Sie kontaktieren, sobald konkrete Termine verfügbar sind.
            </p>
            
            <div className="next-steps">
              <h4>Was passiert als nächstes?</h4>
              <ul>
                <li>Sie erhalten eine Bestätigungs-E-Mail mit Ihrer Registrierung</li>
                <li>Wir stimmen den Termin mit allen Interessierten ab</li>
                <li>Sie erhalten eine Terminbestätigung mit allen Details</li>
                <li>Die verbindliche Anmeldung erfolgt nach Rechnungsstellung</li>
              </ul>
            </div>
            
            <Link 
              to="/ki-werkstatt"
              className="btn btn-primary"
            >
              Zurück zur KI-Werkstatt
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (submitStatus === 'error') {
    return (
      <div className="contact-form-container">
        <div className="registration-form-section error-section">
          <div className="success-content">
            <div className="error-icon">✗</div>
            <h3>Fehler beim Senden</h3>
            <p className="success-message">
              Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt.
            </p>
            <button 
              onClick={() => setSubmitStatus(null)} 
              className="btn btn-primary"
            >
              Erneut versuchen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-form-container">
      <div className="contact-form-content">
        <div className="contact-info">
          <h3>So funktioniert die Registrierung</h3>
          
          <div className="registration-steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>Unverbindliche Reservierung</h4>
                <p>Mit der unverbindlichen Reservierung nehmen wir Sie in die Liste der Interessierten auf, die in der ausgewählten Region in der KI-Werkstatt teilnehmen wollen.</p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>Terminabstimmung</h4>
                <p>Den konkreten Termin der KI-Werkstatt stimmen wir mit den registrierten Interessierten ab.</p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h4>Bestätigung</h4>
                <p>Sie erhalten eine Terminbestätigung mit allen Details zum festgelegten Termin der KI-Werkstatt.</p>
              </div>
            </div>
            
            <div className="step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h4>Verbindliche Anmeldung</h4>
                <p>Sie erhalten eine Rechnung mit der Teilnahmegebühr. Ihre Anmeldung ist mit der Begleichung verbindlich abgeschlossen.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="contact-form">
          <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? 'error' : ''}
                placeholder="Ihr vollständiger Name"
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">E-Mail *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'error' : ''}
                placeholder="ihre.email@beispiel.de"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="company">Firma *</label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className={errors.company ? 'error' : ''}
                placeholder="Ihr Unternehmen"
              />
              {errors.company && <span className="error-message">{errors.company}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="phone">Telefon</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={errors.phone ? 'error' : ''}
                placeholder="+49 (0) 123 456 789"
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="region">Region *</label>
              <select
                id="region"
                name="region"
                value={formData.region}
                onChange={handleChange}
                className={errors.region ? 'error' : ''}
              >
                <option value="">Bitte wählen...</option>
                <option value="Salzkotten">Salzkotten</option>
                <option value="Gütersloh">Gütersloh</option>
                <option value="Remscheid">Remscheid</option>
              </select>
              {errors.region && <span className="error-message">{errors.region}</span>}
              <input type="hidden" name="variant" value={formData.variant} />
            </div>

            <div className="form-group">
              <label htmlFor="participantCount">Anzahl Teilnehmer *</label>
              <input
                type="number"
                id="participantCount"
                name="participantCount"
                value={formData.participantCount}
                onChange={handleChange}
                className={errors.participantCount ? 'error' : ''}
                placeholder="z.B. 2"
                min="1"
                max="50"
              />
              {errors.participantCount && <span className="error-message">{errors.participantCount}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="message">Zusätzliche Nachricht (optional)</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              className={errors.message ? 'error' : ''}
              placeholder="Haben Sie spezielle Anforderungen oder Fragen?"
              rows={4}
            />
            {errors.message && <span className="error-message">{errors.message}</span>}
          </div>

          {/* Honeypot fields - hidden from users */}
          <div style={{ display: 'none' }}>
            <input
              type="text"
              name="website"
              value={formData.website}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
            <input
              type="email"
              name="confirm_email"
              value={formData.confirm_email}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div className="form-group privacy-inline">
            <input
              type="checkbox"
              id="acceptPrivacy"
              name="acceptPrivacy"
              checked={formData.acceptPrivacy || false}
              onChange={handleChange}
              className={errors.acceptPrivacy ? 'error' : ''}
            />
            <label htmlFor="acceptPrivacy" style={{ margin: 0 }}>
              Ich stimme der <a href="/datenschutz" target="_blank" rel="noopener noreferrer">Datenschutzerklärung</a> zu und bin damit einverstanden, dass meine Daten zur Bearbeitung meiner Anfrage gespeichert werden.
            </label>
          </div>
          {errors.acceptPrivacy && <span className="error-message">{errors.acceptPrivacy}</span>}

          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Wird gesendet...' : 'Für Interessentenliste anmelden'}
          </button>
        </form>
        </div>
      </div>
    </div>
  );
};

export default KIWerkstattRegistrationForm;